/**
 * LookCompletionApiService — Wrapper du backend POST /complete-look.
 *
 * Endpoint : `${looksApiUrl}/complete-look` (même HTTP API que /looks)
 * Auth : JWT Cognito obligatoire (récupéré via getAuthToken)
 *
 * Payload :
 *   { selectedItems[], wardrobe[], userProfile{}, context{}, sources[], limitPerCategory }
 *
 * Réponse :
 *   CompleteLookResponse — cf. mytenue-aws-cdk/lambda/shared/look-completion/engine.ts
 *   {
 *     overallScore: 0-100,
 *     overallFeedback: 'FR',
 *     overallFeedbackEn: 'EN',
 *     isComplete: bool,
 *     missingCategories: [
 *       {
 *         category, priority, reason, reasonEn,
 *         suggestions: [{ item, source, score, scoreBreakdown, feedback, feedbackEn }]
 *       }
 *     ],
 *     stats: { ... }
 *   }
 */

import { AWS_CONFIG } from '../config/aws_config';
import * as SecureStore from '../utils/secureStore';
import { hasUsableImage } from '../utils/imageFilter';

const API_BASE = AWS_CONFIG.looksApiUrl;

async function getAuthToken() {
  // Pattern identique à LooksApiService : clé 'outfit_access'
  try {
    return await SecureStore.getItemAsync('outfit_access');
  } catch {
    return null;
  }
}

/**
 * Appelle /complete-look pour suggérer les pièces qui complètent un look.
 *
 * @param {object} args
 * @param {Array} args.selectedItems  - pièces déjà sélectionnées (avec ai_metadata)
 * @param {Array} args.wardrobe       - dressing complet user
 * @param {object} [args.userProfile] - { silhouette, height_cm, weight_kg }
 * @param {object} [args.context]     - { occasion, style, season, tempC }
 * @param {string} [args.gender]      - 'F' | 'M' | 'unisex' (filtre catalogue)
 * @param {Array<'wardrobe'|'catalogue'>} [args.sources] - default ['wardrobe', 'catalogue']
 * @param {number} [args.limitPerCategory] - default 5
 * @returns {Promise<object>} CompleteLookResponse
 */
export async function fetchCompleteLook(args) {
  if (!API_BASE) throw new Error('looksApiUrl manquant dans aws_config.js');

  const token = await getAuthToken();
  if (!token) throw new Error('fetchCompleteLook: JWT requis');

  const url = `${API_BASE}/complete-look`;
  const body = JSON.stringify({
    selectedItems: args.selectedItems || [],
    wardrobe: args.wardrobe || [],
    userProfile: args.userProfile || null,
    context: args.context || {},
    gender: args.gender || 'F',
    sources: args.sources || ['wardrobe', 'catalogue'],
    limitPerCategory: args.limitPerCategory || 5,
  });

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body,
  });

  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`fetchCompleteLook failed (${res.status}): ${errBody.slice(0, 200)}`);
  }

  const data = await res.json();
  // v1.12.977 — garde-fou : retire les suggestions dont l'article n'a pas d'image
  // exploitable (ex. articles Coutr sans visuel) → jamais proposées pour compléter.
  if (data && Array.isArray(data.missingCategories)) {
    data.missingCategories = data.missingCategories.map((c) => ({
      ...c,
      suggestions: Array.isArray(c.suggestions)
        ? c.suggestions.filter((s) => hasUsableImage(s && s.item))
        : c.suggestions,
    }));
  }
  return data;
}
